<script setup>
/**
 * One discrete value in a container, for the two places plain text cannot carry it.
 *
 * Text is the default for anything a record holds: a caption, a list item's supporting text, a cell
 * value. A tag earns its container only when the container does work the text cannot:
 * - several values of one field must read as separate items, and their own commas or parentheses
 *   make a delimited string ambiguous (use HrTagList);
 * - a short label sits on imagery, where text alone has no guaranteed contrast.
 *
 * A tag is never a status. Material 3 shows a record's state as supporting text, and this system
 * adds a component for each case that needs more, listed under avoidWhen.
 *
 * @status recommended
 * @group lists-tables
 * @useWhen Showing several values of one field where a delimited string would be ambiguous (in HrTagList)
 * @useWhen Labelling media with a short attribute that cannot sit on the image as bare text
 * @avoidWhen A caption or supporting text would say it: that is almost always
 * @avoidWhen The label is a record's state: supporting text, or HrSeverityLabel when it is good or bad news
 * @avoidWhen The label singles out one item among its siblings (current, default, recommended): supporting text
 * @avoidWhen The user can change that state: HrStatusActionChip
 * @avoidWhen The value is a count: HrBadge
 * @avoidWhen The label can be tapped, selected or removed: the chip family
 * @related HrTagList
 */
defineOptions({ name: 'HrTag' });

const props = defineProps({
  /**
   * The tag's text. Required: it is what carries the meaning.
   * @example Modeling (MD)
   */
  label: {
    type: String,
    required: true,
  },
});
</script>

<template>
  <span class="hr-tag">
    {{ props.label }}
  </span>
</template>

<style lang="scss" scoped>
// surface-variant is the Material 3 pair for content that sits on the surface without claiming
// attention. There is deliberately no other colour: meaning that needs one is a status, and a
// status is HrSeverityLabel.
//
// One step below a chip's label, which Material 3 sets at label-large. M3 has no tag, and of its
// nearest neighbours a read-only marker sits with a badge (label-small) rather than a chip — so the
// smaller role is what distinguishes the two, not just what looks different.
//
// Set here rather than by the hr-label-* utility class, which only applies once a design-system
// theme is active: the rest of this component sizes itself unconditionally, and the text was the one
// part that inherited whatever surrounded it under the legacy theme.
.hr-tag {
  display: inline-flex;
  align-items: center;
  height: var(--app-comp-tag-container-height);
  padding: 0 var(--app-comp-tag-padding-inline);
  border-radius: var(--app-comp-tag-shape);
  background: var(--app-surface-variant);
  color: var(--app-on-surface-variant);
  font-size: var(--app-comp-tag-label-text-size);
  line-height: var(--app-type-label-small-line-height);
  font-weight: var(--app-type-label-small-weight);
  letter-spacing: var(--app-type-label-small-tracking);
  white-space: nowrap;
}
</style>
